import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

export default function UserDashboard() {
  const { currentUser, getIdToken } = useAuth();
  const [stats, setStats] = useState({ prs: 0, articles: 0, prCredits: 0, guestCredits: 0 });
  const [recentPRs, setRecentPRs] = useState([]);

  useEffect(() => {
    const token = getIdToken();
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API_URL}/api/press-releases/mine.json`, { headers }).catch(() => ({ data: { pressReleases: [] } })),
      axios.get(`${API_URL}/api/articles/mine.json`, { headers }).catch(() => ({ data: { articles: [] } })),
    ]).then(([prRes, artRes]) => {
      const prs = prRes.data.pressReleases || [];
      const arts = artRes.data.articles || [];
      setStats({
        prs: prs.length,
        articles: arts.length,
        prCredits: currentUser?.prCredits || 0,
        guestCredits: currentUser?.guestCredits || 0,
      });
      setRecentPRs(prs.slice(0, 5));
    });
  }, [currentUser, getIdToken]);

  return (
    <div>
      <h2 style={s.pageTitle}>Dashboard</h2>
      <p style={s.subtitle}>Welcome back, <strong>{currentUser?.displayName || currentUser?.email}</strong></p>

      <div style={s.statsGrid}>
        <StatCard label="Press Releases" value={stats.prs} icon="📰" color="#e94560" to="/dashboard/press-releases" />
        <StatCard label="Guest Posts" value={stats.articles} icon="✍" color="#1565c0" to="/dashboard/guest-posts" />
        <StatCard label="PR Credits" value={stats.prCredits} icon="🪙" color="#0d8c6d" to="/dashboard/pr-credits" />
        <StatCard label="Guest Credits" value={stats.guestCredits} icon="🎟" color="#7c3aed" to="/dashboard/guest-credits" />
      </div>

      <div style={s.section}>
        <div style={s.sectionHeader}>
          <h3 style={s.sectionTitle}>Recent Press Releases</h3>
          <Link to="/dashboard/press-release/add" style={s.addBtn}>+ Add New</Link>
        </div>
        {recentPRs.length === 0 ? (
          <div style={s.empty}>
            <p>No press releases yet.</p>
            <Link to="/dashboard/press-release/add" style={s.primaryBtn}>Submit your first press release</Link>
          </div>
        ) : (
          <div style={s.table}>
            <div style={s.tableHead}>
              <span>Headline</span><span>Category</span><span>Status</span><span>Date</span>
            </div>
            {recentPRs.map((pr) => (
              <div key={pr.id} style={s.tableRow}>
                <span style={s.prTitle}>{pr.headline}</span>
                <span style={s.badge}>{pr.category || '—'}</span>
                <StatusBadge status={pr.status} />
                <span style={s.date}>{formatDate(pr.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={s.quickLinks}>
        <h3 style={s.sectionTitle}>Quick Actions</h3>
        <div style={s.quickGrid}>
          <QuickLink to="/dashboard/press-release/add" icon="📰" label="Submit Press Release" />
          <QuickLink to="/dashboard/guest-post/add" icon="✍" label="Submit Guest Post" />
          <QuickLink to="/dashboard/packages/pr" icon="📦" label="Buy PR Package" />
          <QuickLink to="/dashboard/companies" icon="🏢" label="Manage Companies" />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color, to }) {
  return (
    <Link to={to} style={{ ...s.statCard, borderTop: `3px solid ${color}`, textDecoration: 'none' }}>
      <div style={s.statIcon}>{icon}</div>
      <div>
        <div style={{ ...s.statValue, color }}>{value}</div>
        <div style={s.statLabel}>{label}</div>
      </div>
    </Link>
  );
}

function QuickLink({ to, icon, label }) {
  return (
    <Link to={to} style={s.quickCard}>
      <span style={s.quickIcon}>{icon}</span>
      <span style={s.quickLabel}>{label}</span>
    </Link>
  );
}

function StatusBadge({ status }) {
  const map = {
    published:      { bg: '#dcfce7', color: '#15803d' },
    draft:          { bg: '#f3f4f6', color: '#6b7280' },
    pending_review: { bg: '#fef9c3', color: '#b45309' },
    rejected:       { bg: '#fee2e2', color: '#dc2626' },
  };
  const st = map[status] || map.draft;
  return (
    <span style={{ ...s.statusBadge, background: st.bg, color: st.color }}>
      {status ? status.replace('_', ' ') : 'draft'}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const s = {
  pageTitle: { margin: '0 0 0.25rem', fontSize: '1.6rem', color: '#1a1a2e', fontWeight: 700 },
  subtitle: { margin: '0 0 1.75rem', color: '#666', fontSize: '0.9rem' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '1rem', marginBottom: '2rem' },
  statCard: { background: '#fff', borderRadius: 8, padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', color: 'inherit' },
  statIcon: { fontSize: '2rem' },
  statValue: { fontSize: '1.8rem', fontWeight: 700 },
  statLabel: { fontSize: '0.82rem', color: '#888', marginTop: 2 },
  section: { background: '#fff', borderRadius: 8, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '2rem' },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' },
  sectionTitle: { margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' },
  addBtn: { padding: '0.4rem 0.9rem', background: '#e94560', color: '#fff', borderRadius: 4, textDecoration: 'none', fontSize: '0.82rem', fontWeight: 600 },
  primaryBtn: { display: 'inline-block', padding: '0.6rem 1.2rem', background: '#e94560', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600 },
  empty: { textAlign: 'center', padding: '2rem', color: '#888' },
  table: { border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' },
  tableHead: { display: 'grid', gridTemplateColumns: '1fr 120px 130px 110px', padding: '0.6rem 1rem', background: '#f8f8f8', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#999' },
  tableRow: { display: 'grid', gridTemplateColumns: '1fr 120px 130px 110px', padding: '0.75rem 1rem', borderTop: '1px solid #f0f0f0', alignItems: 'center' },
  prTitle: { fontSize: '0.875rem', fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '0.5rem' },
  badge: { fontSize: '0.75rem', color: '#555', background: '#f0f0f0', padding: '0.15rem 0.5rem', borderRadius: 20, display: 'inline-block' },
  statusBadge: { fontSize: '0.75rem', fontWeight: 600, padding: '0.2rem 0.55rem', borderRadius: 20, display: 'inline-block', textTransform: 'capitalize' },
  date: { fontSize: '0.8rem', color: '#888' },
  quickLinks: { background: '#fff', borderRadius: 8, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  quickGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: '1rem' },
  quickCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.25rem', background: '#f8f9fa', borderRadius: 8, textDecoration: 'none', color: '#1a1a2e', border: '1px solid #eee', fontSize: '0.875rem', fontWeight: 600 },
  quickIcon: { fontSize: '1.5rem' },
  quickLabel: { textAlign: 'center', lineHeight: 1.3 },
};
